import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";

import ConditionalButton from "../../../layout/conditional-button";
import ConditionalDropdown from "../../../layout/conditional-dropdown";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";
import BusinessExpenseModal from "../business.expenses/modal";
import CheckRegister from "../check.register";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getAccounts } from "../../../actions/accounts";
import {
  get as getFinancialAccount,
  save as saveFinancialAccount,
  destroy as destroyFinancialAccount,
} from "../../../actions/financial.accounts";

const ACCOUNT_TYPES = ["OPERATING", "TRUST", "ESCROW", "CREDIT_CARD", "OTHER"];

const toCaption = (value) => String(value || "")
  .toLowerCase()
  .split("_")
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

class FinancialAccountItem extends React.Component {
  constructor(props) {
    super(props);
    this.props.loading(true);
    this.state = {
      data: this.mapPropsToState(props),
      accounts: [],
      modal: {
        error: { show: false },
        delete: { show: false },
        businessExpense: { show: false },
      },
      registerUpdated: 0,
    };

    this.props.pageInfo({ title: "Financial Account", breadcrumb: "[New Financial Account]" });
  }

  mapPropsToState = (props) => {
    const {
      uuid,
      name,
      accountType,
      financialInstitution,
      lastFour,
      accountingAccountUuid,
      isTrustAccount,
      isIolta,
      requiresClientLedger,
      defaultTrustLiabilityAccountUuid,
      defaultRevenueAccountUuid,
      defaultExpenseAccountUuid,
      nextCheckNumber,
      isActive,
      meta,
    } = props;

    return {
      uuid,
      name,
      accountType: accountType || "OTHER",
      financialInstitution,
      lastFour,
      accountingAccountUuid,
      isTrustAccount: isTrustAccount ?? false,
      isIolta: isIolta ?? false,
      requiresClientLedger: requiresClientLedger ?? false,
      defaultTrustLiabilityAccountUuid,
      defaultRevenueAccountUuid,
      defaultExpenseAccountUuid,
      nextCheckNumber,
      isActive: isActive ?? true,
      meta,
    };
  };

  get uuid() {
    return this.props.params.uuid;
  }

  accountLabel = (account) => {
    const number = String(account?.accountNumber || "").trim();
    const name = String(account?.name || "").trim();
    const purpose = String(account?.purpose || "").trim();
    const caption = [number, name].filter(Boolean).join(" - ") || account?.uuid || "";
    return purpose ? `${caption} (${toCaption(purpose)})` : caption;
  };

  loadAccounts = async () => {
    const accounts = await this.props.getAccounts(null);
    this.setState({ accounts: accounts || [] });
  };

  loadData = async () => {
    this.props.loading(true);

    if (isGuid(this.uuid)) {
      const result = await this.props.getFinancialAccount(this.uuid);
      const item = result?.item || result;

      this.setState({ data: { ...item } });

      const caption = item?.name || item?.uuid;
      await this.props.pageInfo({ title: `Financial Account (${caption})`, breadcrumb: `${caption}` });
    }

    this.props.loading(false);
  };

  componentDidMount = () => {
    setTimeout(async () => {
      await this.loadAccounts();
      await this.loadData();
    }, 30);
  };

  componentDidUpdate = (oldProps) => {
    if (oldProps?.params?.uuid !== this.props?.params?.uuid) this.componentDidMount();
  };

  onChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      data: {
        ...prev.data,
        [name]: value,
      },
    }));
  };

  onCheckChange = (e) => {
    const { name, checked } = e.target;
    this.setState((prev) => ({
      data: {
        ...prev.data,
        [name]: checked,
      },
    }));
  };

  get accountTypeOptions() {
    return ACCOUNT_TYPES.map((accountType) => ({ value: accountType, label: toCaption(accountType) }));
  }

  get selectedAccountTypeOption() {
    return this.accountTypeOptions.find((option) => option.value === (this.state.data?.accountType || "OTHER")) || this.accountTypeOptions[0];
  }

  onAccountTypeChange = (option) => {
    this.onChange({ target: { name: "accountType", value: option?.value || "OTHER" } });
  };

  accountOptions = (filter = () => true) => (this.state.accounts || [])
    .filter((account) => account?.uuid && filter(account))
    .map((account) => ({
      value: account.uuid,
      label: this.accountLabel(account),
    }));

  selectedAccountOption = (fieldName, options) => {
    const uuid = this.state.data?.[fieldName];
    if (!isGuid(uuid)) return null;
    return options.find((option) => option.value === uuid) || null;
  };

  onAccountChange = (fieldName) => (option) => {
    this.onChange({ target: { name: fieldName, value: option?.value || null } });
  };

  showError = (message) => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: {
          title: "Financial account error",
          body: message,
          show: true,
        },
      },
    }));
  };

  onSaveClick = async () => {
    const data = this.state.data || {};

    if (!String(data.name || "").trim()) {
      this.showError("Financial account name is required.");
      return;
    }

    if (!ACCOUNT_TYPES.includes(data.accountType)) {
      this.showError("Financial account type is required.");
      return;
    }

    this.props.loading(true);

    const payload = {
      ...data,
      uuid: isGuid(this.uuid) ? this.uuid : uuidv4(),
      accountingAccountUuid: isGuid(data.accountingAccountUuid) ? data.accountingAccountUuid : null,
      defaultTrustLiabilityAccountUuid: isGuid(data.defaultTrustLiabilityAccountUuid) ? data.defaultTrustLiabilityAccountUuid : null,
      defaultRevenueAccountUuid: isGuid(data.defaultRevenueAccountUuid) ? data.defaultRevenueAccountUuid : null,
      defaultExpenseAccountUuid: isGuid(data.defaultExpenseAccountUuid) ? data.defaultExpenseAccountUuid : null,
      isTrustAccount: Boolean(data.isTrustAccount),
      isIolta: Boolean(data.isIolta),
      requiresClientLedger: Boolean(data.requiresClientLedger),
      isActive: data.isActive ?? true,
    };

    const saved = await this.props.saveFinancialAccount(payload);

    if (saved?.success === false) {
      this.props.loading(false);
      this.showError(saved?.error?.message || "The financial account could not be saved.");
      return;
    }

    const id = saved?.uuid ?? payload.uuid;
    window.location.href = `/dashboard/accounting/financial-accounts/${id}`;
  };

  onDeleteClick = () => {
    const caption = this.state.data?.name || this.uuid;

    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: {
          title: "Delete financial account",
          body: `This action will permanently delete the financial account ${caption}.`,
          show: true,
        },
      },
    }));
  };

  onDeleteOkModal = async () => {
    await this.props.destroyFinancialAccount(this.state.data?.uuid || this.uuid);
    this.onDeleteHideModal();
    this.props.navigate("/dashboard/accounting/financial-accounts");
  };

  onDeleteHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: { show: false },
      },
    }));
  };

  onErrorHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: { show: false },
      },
    }));
  };

  get newItems() {
    return [
      {
        caption: "Business Expense",
        onClick: this.onBusinessExpenseClick,
      },
    ];
  }

  onBusinessExpenseClick = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        businessExpense: { show: true },
      },
    }));
  };

  onBusinessExpenseHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        businessExpense: { show: false },
      },
    }));
  };

  onBusinessExpenseSaved = () => {
    this.onBusinessExpenseHideModal();
    this.setState((prev) => ({ registerUpdated: prev.registerUpdated + 1 }));
  };

  renderAccountSelect = ({ fieldName, label, filter, placeholder }) => {
    const options = this.accountOptions(filter);

    return (
      <div className="mb-3 top-label">
        <Select
          inputId={fieldName}
          classNamePrefix="react-select"
          isClearable
          placeholder={placeholder || "(No account)"}
          options={options}
          value={this.selectedAccountOption(fieldName, options)}
          onChange={this.onAccountChange(fieldName)}
        />
        <Form.Label>{label}</Form.Label>
      </div>
    );
  };

  render() {
    const d = this.state.data || {};

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalDropdown
            className="ms-sm-1"
            enabledVariant="outline-primary"
            display={isGuid(this.state.data?.uuid || this.uuid)}
            enabled
            items={this.newItems}
            icon="plus"
          >
            New
          </ConditionalDropdown>

          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Financial Account"
            display={true}
            enabled={true}
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>

          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Delete Financial Account"
            display={true}
            enabled={isGuid(this.uuid)}
            onEnabledClick={this.onDeleteClick}
            icon="bin"
          >
            Delete
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="">
            <Col xs="12" md="8" xl="5">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="name"
                  value={d?.name || ""}
                  onChange={this.onChange}
                />
                <Form.Label>NAME</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="4" xl="3">
              <div className="mb-3 top-label">
                <Select
                  inputId="accountType"
                  classNamePrefix="react-select"
                  isClearable={false}
                  options={this.accountTypeOptions}
                  value={this.selectedAccountTypeOption}
                  onChange={this.onAccountTypeChange}
                />
                <Form.Label>TYPE</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6" xl="3">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="financialInstitution"
                  value={d?.financialInstitution || ""}
                  onChange={this.onChange}
                />
                <Form.Label>FINANCIAL INSTITUTION</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6" xl="1">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="lastFour"
                  value={d?.lastFour || ""}
                  maxLength={4}
                  onChange={this.onChange}
                />
                <Form.Label>LAST 4</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6">
              {this.renderAccountSelect({
                fieldName: "accountingAccountUuid",
                label: "ACCOUNTING ACCOUNT",
                placeholder: "(Select cash/accounting account)",
                filter: (account) => account.type === "ASSET",
              })}
            </Col>

            <Col xs="12" md="6">
              {this.renderAccountSelect({
                fieldName: "defaultTrustLiabilityAccountUuid",
                label: "DEFAULT TRUST LIABILITY ACCOUNT",
                placeholder: "(Select trust liability account)",
                filter: (account) => account.type === "LIABILITY",
              })}
            </Col>

            <Col xs="12" md="6">
              {this.renderAccountSelect({
                fieldName: "defaultRevenueAccountUuid",
                label: "DEFAULT REVENUE ACCOUNT",
                placeholder: "(Select revenue account)",
                filter: (account) => account.type === "REVENUE",
              })}
            </Col>

            <Col xs="12" md="6">
              {this.renderAccountSelect({
                fieldName: "defaultExpenseAccountUuid",
                label: "DEFAULT EXPENSE ACCOUNT",
                placeholder: "(Select expense account)",
                filter: (account) => ["EXPENSE", "COGS"].includes(account.type),
              })}
            </Col>

            <Col xs="12" md="3">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="nextCheckNumber"
                  value={d?.nextCheckNumber || ""}
                  onChange={this.onChange}
                />
                <Form.Label>NEXT CHECK NUMBER</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="3">
              <Form.Check
                type="switch"
                id="financial-account-trust"
                name="isTrustAccount"
                label="Trust Account"
                checked={Boolean(d?.isTrustAccount)}
                onChange={this.onCheckChange}
              />
            </Col>

            <Col xs="12" md="3">
              <Form.Check
                type="switch"
                id="financial-account-iolta"
                name="isIolta"
                label="IOLTA"
                checked={Boolean(d?.isIolta)}
                onChange={this.onCheckChange}
              />
            </Col>

            <Col xs="12" md="3">
              <Form.Check
                type="switch"
                id="financial-account-client-ledger"
                name="requiresClientLedger"
                label="Requires Client Ledger"
                checked={Boolean(d?.requiresClientLedger)}
                onChange={this.onCheckChange}
              />
            </Col>

            <Col xs="12" md="3">
              <Form.Check
                type="switch"
                id="financial-account-active"
                name="isActive"
                label="Active"
                checked={Boolean(d?.isActive)}
                onChange={this.onCheckChange}
              />
            </Col>

            <CheckRegister
              embedded
              financialAccountUuid={this.state.data?.uuid || this.uuid}
              updated={this.state.registerUpdated}
            />
          </Row>
        </Col>

        <ErrorModal
          show={this.state.modal.error.show}
          title={this.state.modal.error.title}
          body={this.state.modal.error.body}
          buttons={[
            {
              default: true,
              caption: "ok",
              onEnabledClick: this.onErrorHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        ></ErrorModal>

        <DeleteModal
          show={this.state.modal.delete.show}
          title={this.state.modal.delete.title}
          body={this.state.modal.delete.body}
          buttons={[
            {
              caption: "ok",
              onEnabledClick: this.onDeleteOkModal,
              variant: "primary",
              display: true,
              enabled: true,
            },
            {
              default: true,
              caption: "cancel",
              onEnabledClick: this.onDeleteHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        ></DeleteModal>

        <BusinessExpenseModal
          show={this.state.modal.businessExpense.show}
          defaultFinancialAccountUuid={this.state.data?.uuid || this.uuid}
          defaultExpenseAccountUuid={this.state.data?.defaultExpenseAccountUuid}
          onCancel={this.onBusinessExpenseHideModal}
          onSaved={this.onBusinessExpenseSaved}
        />
      </>
    );
  }
}

FinancialAccountItem.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, {
    pageInfo,
    loading,
    getAccounts,
    getFinancialAccount,
    saveFinancialAccount,
    destroyFinancialAccount,
  })(FinancialAccountItem)
);
